import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SalaBancaViewModel } from '../../../viewmodel/banca/SalaBancaViewModel';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { FaCopy, FaLink, FaCheck } from 'react-icons/fa';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';

interface DefinirSalaModalProps {
  banca: any;
  processoId: string;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const DefinirSalaModal: React.FC<DefinirSalaModalProps> = ({ 
  banca, 
  processoId,
  onClose, 
  onSaveSuccess 
}) => {
  const [salaUrl, setSalaUrl] = useState<string>('');
  const [originalUrl, setOriginalUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const hasUrl = originalUrl.trim() !== '';
  const hasChanges = salaUrl !== originalUrl;
  const isValidUrl = salaUrl.trim() === '' || /^https?:\/\/.+/.test(salaUrl.trim());

  useEffect(() => {
    const loadSala = async () => {
      try {
        const url = banca.salaDeBanca?.link || await SalaBancaViewModel.getSalaBanca(processoId, banca.id) || '';
        setSalaUrl(url);
        setOriginalUrl(url);
      } catch (error) {
        console.error('Erro ao carregar sala:', error);
        setError('Erro ao carregar dados da sala');
      } finally {
        setInitialLoading(false);
      }
    };
    loadSala();
  }, [banca]);

  const handleSave = async () => {
    if (!isValidUrl) {
      setError('URL deve começar com http:// ou https://');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await SalaBancaViewModel.patchSalaBanca(processoId, banca.id, salaUrl.trim());
      setOriginalUrl(salaUrl.trim());
      toast.success('Sala da banca atualizada com sucesso!');
      onSaveSuccess();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setError(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(salaUrl);
      toast.success('URL copiada!');
    } catch {
      toast.error('Erro ao copiar URL');
    }
  };

  const handleReset = () => {
    setSalaUrl(originalUrl);
    setError('');
  };

  if (initialLoading) {
    return (
      <AnimatedModal isOpen={true} onClose={onClose}>
        <div style={{
          backgroundColor: 'white', padding: '40px', borderRadius: '8px',
          textAlign: 'center', minWidth: '200px'
        }}>
          Carregando...
        </div>
      </AnimatedModal>
    );
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white', padding: '32px', borderRadius: '12px',
        width: '520px', maxWidth: '90vw', boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ 
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '48px', height: '48px', borderRadius: '50%',
            backgroundColor: '#e3f2fd', marginBottom: '12px'
          }}>
            <FaLink style={{ color: '#1976d2', fontSize: '20px' }} />
          </div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            {hasUrl ? 'Gerenciar Sala da Banca' : 'Definir Sala da Banca'}
          </h3>
        </div>

        {/* URL Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', marginBottom: '8px', 
            fontWeight: '500', color: '#374151' 
          }}>
            URL da Sala Virtual:
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="url"
              value={salaUrl}
              onChange={(e) => {
                setSalaUrl(e.target.value);
                setError('');
              }}
              placeholder="https://meet.google.com/abc-defg-hij"
              style={{
                width: '100%', padding: '12px 40px 12px 16px', fontSize: '14px',
                border: `2px solid ${error ? '#ef4444' : (isValidUrl ? '#d1d5db' : '#f59e0b')}`,
                borderRadius: '8px', outline: 'none',
                transition: 'border-color 0.2s'
              }}
            />
            {salaUrl && (
              <button
                onClick={handleCopy}
                style={{
                  position: 'absolute', right: '8px', top: '50%',
                  transform: 'translateY(-50%)', padding: '8px',
                  backgroundColor: 'transparent', border: 'none',
                  cursor: 'pointer', color: '#6b7280'
                }}
                title="Copiar URL"
              >
                <FaCopy />
              </button>
            )}
          </div>
          {error && (
            <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px' }}>
              {error}
            </div>
          )}
          {!isValidUrl && !error && (
            <div style={{ color: '#f59e0b', fontSize: '13px', marginTop: '4px' }}>
              URL deve começar com http:// ou https://
            </div>
          )}
        </div>

        {/* Status */}
        {hasUrl && (
          <div style={{
            backgroundColor: '#f0f9ff', padding: '12px 16px',
            borderRadius: '8px', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <FaCheck style={{ color: '#0ea5e9', fontSize: '14px' }} />
            <span style={{ fontSize: '14px', color: '#0c4a6e' }}>
              Sala configurada e pronta para uso
            </span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', border: '1px solid #d1d5db',
              backgroundColor: 'white', borderRadius: '6px',
              cursor: 'pointer', fontSize: '14px', fontWeight: '500'
            }}
            disabled={loading}
          >
            Cancelar
          </button>
          
          {hasChanges && (
            <button
              onClick={handleReset}
              style={{
                padding: '10px 20px', border: '1px solid #d1d5db',
                backgroundColor: '#f9fafb', borderRadius: '6px',
                cursor: 'pointer', fontSize: '14px', fontWeight: '500'
              }}
              disabled={loading}
            >
              Desfazer
            </button>
          )}
          
          <button
            onClick={handleSave}
            disabled={loading || !hasChanges || !isValidUrl}
            style={{
              padding: '10px 20px', backgroundColor: '#059669',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: loading || !hasChanges || !isValidUrl ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500',
              opacity: loading || !hasChanges || !isValidUrl ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            {loading ? 'Salvando...' : (
              <>
                <FaCheck style={{ fontSize: '12px' }} />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default DefinirSalaModal;